import {
  IconArrowRight,
  IconArrowRotateRightLeft,
  IconArrowCornerDownRight,
  IconChevronRight,
  GreyGrey20,
  GreyGrey30,
  GreyGrey60,
  GreyGrey80,
  Primary13SemiBold,
  Primary13Regular,
  Primary16SemiBold,
  SequenceMenu,
  ShadowXs,
  Skeleton,
  TextOverflow,
  Tooltip,
  Table,
  TableContainer,
  Tbody,
  Td,
  Th,
  Thead,
  Tr,
  Box,
  Flex,
  Grid,
  GridItem
} from '@sequencehq/core-components'
import {
  TablePhase,
  TableProductInformation,
  useSchedulePricingSection
} from './useSchedulePricingSection'
import { useCallback, useState } from 'react'
import fill from 'lodash/fill'
import { SectionError } from '../../common/SectionError'
import { formatOrdinal } from '@sequencehq/utils'
import { useBillingScheduleId } from 'BillingScheduleHome/utils/useBillingScheduleId'
import { match } from 'ts-pattern'
import { ProductPriceDrawer } from 'Products/index'

const PhaseDateSummary = (props: {
  dateSummary: TablePhase['dateSummary']
}) => {
  if (!props.dateSummary.endDate || props.dateSummary.isAfterMilestonePhase) {
    return <Box>{props.dateSummary.duration}</Box>
  }

  return (
    <Flex alignItems="center" gap="4px">
      <Box>{props.dateSummary.startDate}</Box>
      <IconArrowRight color="inherit" />
      <Box>{props.dateSummary.endDate}</Box>
    </Flex>
  )
}

const ProductPriceSummary = (props: {
  priceSummary: TableProductInformation['summary']
}) => {
  if (!props.priceSummary.totalAfterDiscount) {
    return (
      <Box overflow="hidden" textOverflow="ellipsis" whiteSpace="nowrap">
        {props.priceSummary.total} / {props.priceSummary.billingFrequency} (
        {props.priceSummary.billingType})
      </Box>
    )
  }

  return (
    <Box overflow="hidden" textOverflow="ellipsis" whiteSpace="nowrap">
      <Box
        as="span"
        textDecoration="line-through"
        color={GreyGrey60}
        marginRight="4px"
      >
        {props.priceSummary.total}
      </Box>
      {props.priceSummary.totalAfterDiscount} /{' '}
      {props.priceSummary.billingFrequency} ({props.priceSummary.billingType})
    </Box>
  )
}

const PriceKebabMenu = (props: {
  priceId: string
  onSelectPrice: (priceId: string) => void
}) => {
  return (
    <SequenceMenu
      data-testid={`schedule.prices.${props.priceId}.menu`}
      variant="ghost"
      alignment="bottom-right"
      buttonSize="small"
      items={[
        ({ setIsOpen }) => (
          <SequenceMenu.Item
            key="view-price"
            uuid="view-schedule"
            label="View price"
            data-testid={`schedule.prices.${props.priceId}.menuItem.view`}
            onClick={() => {
              props.onSelectPrice(props.priceId)
              setIsOpen?.(false)
            }}
          />
        )
      ]}
    />
  )
}

export const PhaseTable = (props: {
  phase: TablePhase
  mountExpanded?: boolean
  /**
   * interactive (default: true)
   * when true, rows are clickable and kebab menus are rendered
   * @type {boolean}
   */
  interactive?: boolean
  /**
   * variant (default: 'standard')
   * changes the default column widths for use in space-limited contexts
   * @type {('standard' | 'compact')}
   */
  variant?: 'standard' | 'compact'
  onSelectPrice: (priceId: string) => void
}) => {
  const {
    mountExpanded = false,
    phase,
    interactive = true,
    variant = 'standard',
    onSelectPrice
  } = props

  const [expanded, setExpanded] = useState(mountExpanded)
  const onRowClick = useCallback(
    (priceId: string) => {
      if (!interactive) return
      onSelectPrice(priceId)
    },
    [onSelectPrice, interactive]
  )

  const columnWidths = match(variant)
    .with('standard', () => ({
      discount: '148px',
      amount: '350px',
      kebabMenu: '52px'
    }))
    .with('compact', () => ({
      discount: '100px',
      amount: '280px',
      kebabMenu: '52px'
    }))
    .exhaustive()

  return (
    <Box data-testid="schedule.prices">
      <Grid
        gridTemplateColumns={
          phase.name ? 'auto auto auto 1fr auto' : 'auto auto 1fr auto'
        }
        gap="8px"
        alignItems="center"
        cursor="pointer"
        onClick={() => setExpanded(!expanded)}
        userSelect="none"
        height="32px"
      >
        <GridItem
          transform={expanded ? 'rotateZ(90deg)' : 'rotateZ(0)'}
          transition="transform 250ms ease-in-out"
        >
          <IconChevronRight />
        </GridItem>
        <GridItem {...Primary13SemiBold} color={GreyGrey80}>
          {phase.name ? (
            phase.name
          ) : (
            <PhaseDateSummary dateSummary={phase.dateSummary} />
          )}
        </GridItem>
        {props.phase.name && (
          <GridItem>
            <Box {...Primary13Regular} color={GreyGrey60} position="relative">
              <PhaseDateSummary dateSummary={phase.dateSummary} />
            </Box>
          </GridItem>
        )}
        <GridItem display="flex" alignItems="center">
          {phase.isPhaseBillingReset && (
            <Tooltip label="Billing period reset" placement="top">
              <Box
                color={GreyGrey60}
                _hover={{
                  backgroundColor: GreyGrey20
                }}
                transition="background-color 0.25s ease-in-out"
                padding="4px"
                borderRadius="4px"
              >
                <IconArrowRotateRightLeft />
              </Box>
            </Tooltip>
          )}
        </GridItem>
        {expanded && (
          <GridItem>
            {phase.recurrenceDayOfMonth ? (
              <Box {...Primary13Regular} color={GreyGrey60}>
                Billing day: {formatOrdinal(phase.recurrenceDayOfMonth)} of the
                month
              </Box>
            ) : (
              <Skeleton height="24px" width="160px" />
            )}
          </GridItem>
        )}
      </Grid>
      <Box
        height={expanded ? 'fit-content' : '0px'}
        css={{
          /**
            This has limited browser support for now - basically chromium only,
            but it's just a bit of bonus bacon that transitions to height nicely.
          */
          interpolateSize: 'allow-keywords'
        }}
        transition={'height 0.25s ease-in-out'}
        overflow="hidden"
      >
        <TableContainer
          borderRadius="lg"
          overflow="hidden"
          border={`1px solid ${GreyGrey30}`}
          margin="8px 0"
          width="100%"
          boxShadow={ShadowXs}
        >
          <Table variant="v2" width="100%">
            <Thead userSelect="none">
              <Tr>
                <Th>Description</Th>
                <Th width={columnWidths.discount} textAlign="right">
                  {phase.phaseHasDiscount ? 'Discount' : ''}
                </Th>
                <Th width={columnWidths.amount} textAlign="right">
                  Amount
                </Th>
                {interactive && <Th width={columnWidths.kebabMenu}></Th>}
              </Tr>
            </Thead>
            <Tbody>
              {phase.productsWithNoMinimum.map(product => (
                <Tr
                  key={product.id}
                  data-testid={`schedule.phase.${phase.id}.prices.${product.priceId}`}
                  onClick={() => onRowClick(product.priceId)}
                  cursor={!interactive ? 'default !important' : undefined}
                >
                  <Td>
                    <TextOverflow>{product.name}</TextOverflow>
                  </Td>
                  <Td textAlign="right">{product.summary.discount}</Td>
                  <Td
                    textAlign="right"
                    data-testid={`schedule.phase.${phase.id}.prices.${product.priceId}.priceAmount`}
                  >
                    <ProductPriceSummary priceSummary={product.summary} />
                  </Td>
                  {interactive && (
                    <Td padding="0 12px !important">
                      <PriceKebabMenu
                        priceId={product.priceId}
                        onSelectPrice={onSelectPrice}
                      />
                    </Td>
                  )}
                </Tr>
              ))}
              {phase.minimum && (
                <Tr
                  pointerEvents="none"
                  cursor={!interactive ? 'default !important' : undefined}
                  data-testid={`schedule.phase.${phase.id}.prices.minimum`}
                >
                  <Td>Minimum commitment</Td>
                  <Td></Td>
                  <Td textAlign="right">
                    {phase.minimum.summary.minimumAmount} /{' '}
                    {phase.minimum.summary.billingFrequency}
                  </Td>
                  {interactive && <Td></Td>}
                </Tr>
              )}
              {phase.minimum?.products.map(product => (
                <Tr
                  key={product.id}
                  onClick={() => onRowClick(product.priceId)}
                  cursor={!interactive ? 'default !important' : undefined}
                  data-testid={`schedule.phase.${phase.id}.prices.${product.priceId}`}
                >
                  <Td>
                    <Flex gap="8px">
                      <Flex flexShrink="0">
                        <IconArrowCornerDownRight color={GreyGrey60} />
                      </Flex>
                      <TextOverflow>{product.name}</TextOverflow>
                    </Flex>
                  </Td>
                  <Td textAlign="right">{product.summary.discount}</Td>
                  <Td
                    textAlign="right"
                    data-testid={`schedule.phase.${phase.id}.prices.${product.priceId}.priceAmount`}
                  >
                    <ProductPriceSummary priceSummary={product.summary} />
                  </Td>
                  {interactive && (
                    <Td padding="0 12px !important">
                      <PriceKebabMenu
                        priceId={product.priceId}
                        onSelectPrice={onSelectPrice}
                      />
                    </Td>
                  )}
                </Tr>
              ))}
              {phase.globalDiscount && (
                <Tr
                  pointerEvents="none"
                  cursor={!interactive ? 'default !important' : undefined}
                  data-testid={`schedule.phase.${phase.id}.prices.globalDiscount`}
                >
                  <Td>Global discount</Td>
                  <Td textAlign="right">{phase.globalDiscount.amount}</Td>
                  <Td></Td>
                  {interactive && <Td></Td>}
                </Tr>
              )}
            </Tbody>
          </Table>
        </TableContainer>
      </Box>
    </Box>
  )
}

export const SchedulePricingSection = () => {
  const billingScheduleId = useBillingScheduleId()
  const hook = useSchedulePricingSection({ billingScheduleId })
  const [selectedPriceId, setSelectedPriceId] = useState<string | null>(null)

  if (hook.isLoading) {
    return (
      <Box>
        <Box {...Primary16SemiBold} marginBottom="16px">
          Pricing
        </Box>
        <Skeleton height="24px" width="240px" />
        <Flex
          gap="8px"
          marginBottom="24px"
          marginTop="16px"
          flexDirection="column"
        >
          {fill(Array(3), null).map((_, idx) => (
            <Skeleton height="36px" width="100%" key={idx} />
          ))}
        </Flex>
        <Skeleton height="24px" width="120px" />
        <Flex gap="8px" marginTop="16px" flexDirection="column">
          {fill(Array(4), null).map((_, idx) => (
            <Skeleton height="36px" width="100%" key={idx} />
          ))}
        </Flex>
      </Box>
    )
  }

  if (hook.isError) {
    return (
      <Box>
        <Box {...Primary16SemiBold} marginBottom="16px">
          Pricing
        </Box>
        <Box
          borderRadius="lg"
          padding="16px"
          overflow="hidden"
          border={`1px solid ${GreyGrey30}`}
        >
          <SectionError
            size="medium"
            text="An error occurred when loading the pricing information"
          />
        </Box>
      </Box>
    )
  }

  return (
    <Box>
      <Box {...Primary16SemiBold} marginBottom="16px">
        Pricing
      </Box>
      <Grid gridTemplateRows="auto">
        {hook.phases?.map(phase => (
          <PhaseTable
            key={phase.id}
            phase={phase}
            mountExpanded={hook.initialPhaseToExpand === phase.id}
            onSelectPrice={setSelectedPriceId}
          />
        ))}
      </Grid>
      {selectedPriceId && (
        <ProductPriceDrawer
          priceId={selectedPriceId}
          onClose={() => setSelectedPriceId(null)}
          showBillingScheduleInfo={false}
        />
      )}
    </Box>
  )
}
